import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SITE, STORY_PHOTOS } from "@/data/site";

const CHAPTERS = [
  {
    no: "01",
    title: "Her Şey Bir Cuma Günü Başladı",
    paragraphs: [
      "İşimden yeni ayrılmıştım. Yeni bir iş arayışı içindeydim.",
      "Bir cuma günü, o dönemlerin meşhur mekânlarından Otel Basri'de bir görüşmeye gittim. Görüşme yaklaşık 15–20 dakika sürdü. Çok kısa bir süre içerisinde iş olgunlaştı.",
      "Onların sektör uzmanlığı, benim satış ve pazarlama tecrübemle birleşti.",
      "Ve saat sektöründeki yolculuğum böyle başladı.",
    ],
  },
  {
    no: "02",
    title: "Öğrenmek, Gelişmek ve 20 Yıl",
    paragraphs: [
      "Mağaza müdürü olarak başladığım bu yolculukta işin yalnızca satıştan ibaret olmadığını öğrendim.",
      "Satış ve pazarlama, ürün bilgisi, insan ilişkileri, teknik servis, vitrin düzeni ve daha birçok konuda, işin gerçekten uzmanlarından eğitimler aldık.",
      "Yıllar boyunca öğrendik, uyguladık ve kendimizi geliştirdik.",
      "Sektörün hırslı, başarı ve satış odaklı bir yapısının içerisinde çalışmak insanı ister istemez güçlü olmaya sevk ediyor.",
      "Elbette her tecrübenin insandan götürdüğü şeyler de var.",
      "Ama aynı zamanda size birçok meziyet katıyor; bazen de içinizde olduğunu bilmediğiniz yönlerin ortaya çıkmasını sağlıyor.",
      "Mağaza müdürü olarak başladığım görevimi 20 yıl boyunca sürdürdüm.",
      "Bir pozisyonda bu kadar uzun süre kalabilmek kolay değil.",
      "Benim için bu 20 yıl, yalnızca bir çalışma dönemi değil; insan, satış, yönetim, ürün ve saatçilik konusunda bir okul oldu.",
    ],
  },
  {
    no: "03",
    title: "Bir Kararın Arkasında Durmak",
    paragraphs: [
      "Bu yolculuktaki önemli dönüm noktalarından biri de mağazanın lokasyonuyla ilgili verdiğim karardı.",
      "O dönemde benim gördüğüm bir fırsat vardı.",
      "Koordinatör müdürümün dahi o anda göremediği bu fırsatın arkasında durdum ve vazgeçmedim.",
      "O gün kapatılması düşünülen mağazanın, bu karar ve mücadele sayesinde 12–13 yıl daha hizmet vermesinin önü açıldı.",
      "Bugün geriye dönüp baktığımda bunun bana öğrettiği şey çok net:",
      "Bazen doğru olduğuna inandığınız bir kararın arkasında durmak, sonucu değiştirebilir.",
    ],
  },
  {
    no: "04",
    title: "Bir Başka Dönüm Noktası: Ayrılmak",
    paragraphs: [
      "Ve sonra bir gün, 20 yıllık bir dönemin ardından işten ayrıldım.",
      "Bu da hayatımdaki önemli dönüm noktalarından biriydi.",
      "Çünkü insan bazen alıştığı düzeni bırakmak zorunda kalıyor.",
      "Ama bazı bitişler, aslında yeni bir başlangıcın kapısını açıyor.",
    ],
  },
  {
    no: "05",
    title: "2025: Yeni Bir Kapı",
    paragraphs: [
      "Beni 2006 yılında işe alan kişi, yıllar sonra 2025'te, benim ısrarlarım sonucunda Essa / Daniel Klein'ın Türkiye'deki ilk ve tek plan franchise mağazasının kapılarını açtı.",
      "Bu bizim için gerçekten büyük bir fırsattı.",
      "Fakat ilk ve tek olmak, beraberinde kendi zorluklarını da getirdi.",
      "Organizasyonun perakende tarafının henüz çok yeni olması, mağazanın işleyişini oturtma konusunda bizi birçok kez zorladı.",
      "İşin kuruluşundan işleyişine kadar birçok konuda, AVM yönetimi dahil olmak üzere çok az kişiden destek gördük.",
      "Ama yılmadık.",
      "Yılmayacağız.",
      "Çünkü bugüne kadar öğrendiğimiz en önemli şeylerden biri şu:",
      "İlk olmak kolay değildir. Ama bir şeyi ilk yapan olmak, aynı zamanda kendi yolunu açmak demektir.",
      "Allah utandırmasın.",
    ],
  },
  {
    no: "06",
    title: "Yeni Bir Dünya: E-Ticaret",
    paragraphs: [
      "Sonra bir gün internette gezinirken e-ticaret konusunda bir reklam gördüm.",
      "O reklamdan çıkan küçük bir kıvılcım, kafamda yeni sorular oluşturmaya başladı:",
      "“Daha nasıl yaparız?”",
      "“Bunu nasıl geliştiririz?”",
      "Ve kendimi, neredeyse sıfır e-ticaret ve bilgisayar bilgisiyle, Shopify ve Amazon'da mağaza açarken buldum.",
      "Yaklaşık üç aydır gece gündüz öğrenmeye, denemeye, geliştirmeye çalışıyorum.",
      "Bazen yoruluyorum.",
      "Bazen “artık yeter” dediğim oluyor.",
      "Ama sonra yeniden başlıyorum.",
      "Çünkü 20 yıl önce saatçilik sektörüne girerken de her şeyi bilmiyordum.",
      "Öğrendim.",
      "Şimdi de öğreniyorum.",
    ],
  },
  {
    no: "07",
    title: "Ve Bugün…",
    paragraphs: [
      "Bugün Atılmaz Saat'in hikâyesi hâlâ yazılıyor.",
      "2006'da bir cuma günü başlayan yolculuk, bugün mağazacılıktan teknik servise, e-ticaretten dijital dünyaya uzanıyor.",
      "Değişen çok şey oldu.",
      "Ama değişmeyen bir şey var:",
      "Öğrenmekten, denemekten ve mücadele etmekten vazgeçmemek.",
      "Çünkü bizim için saat yalnızca zamanı gösteren bir aksesuar değil.",
      "Bazen bir başlangıçtır.",
      "Bazen bir hediye.",
      "Bazen yıllardır saklanan bir hatıra.",
      "Ve bazen de, tıpkı bizim hikâyemizde olduğu gibi, hayatın yeni bir döneminin başlangıcıdır.",
    ],
  },
];

const isPunchline = (p) => p.length <= 28 && !p.startsWith("“");

export default function HikayemizPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="story-page">
      <Helmet>
        <title>Hikâyemiz | Atılmaz Saat — 2006'dan Bugüne</title>
        <meta
          name="description"
          content="Bir saatçilik hikâyesi: 2006'da bir cuma günü başlayan yolculuk, bugün mağazacılıktan teknik servise, e-ticaretten dijital dünyaya uzanıyor."
        />
      </Helmet>
      {/* Başlık */}
      <section className="relative overflow-hidden bg-emerald text-ivory pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="grain-overlay" />
        <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none select-none">
          <p className="font-serif text-[18vw] leading-[0.8] font-semibold text-outline-ivory whitespace-nowrap translate-y-[18%]">
            2006
          </p>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ivory/50">Hikâyemiz</p>
            <h1 className="mt-4 font-serif text-3xl sm:text-5xl lg:text-6xl font-semibold tracking-tight uppercase leading-[1.1]">
              Atılmaz Saat —<br />2006'dan Bugüne
            </h1>
            <p className="mt-5 font-serif text-2xl sm:text-3xl italic leading-snug text-gold">
              Bir Saatçilik Hikâyesi
            </p>
          </Reveal>
        </div>
      </section>

      {/* Bölümler */}
      <section className="bg-ivory py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {CHAPTERS.map((c, ci) => (
            <article key={c.no} data-testid={`story-chapter-${c.no}`} className={ci > 0 ? "mt-20 md:mt-28" : ""}>
              {ci > 0 && (
                <div className="mb-14 md:mb-20 flex items-center gap-4" aria-hidden="true">
                  <span className="h-px flex-1 bg-emerald/10" />
                  <span className="h-1.5 w-1.5 rotate-45 bg-gold/60" />
                  <span className="h-px flex-1 bg-emerald/10" />
                </div>
              )}
              <Reveal>
                <div className="flex items-baseline gap-5">
                  <span className="font-serif text-5xl md:text-6xl font-semibold text-emerald/10 leading-none shrink-0">
                    {c.no}
                  </span>
                  <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-ink uppercase leading-tight">
                    {c.title}
                  </h2>
                </div>
              </Reveal>
              <div className="mt-8 md:mt-10 md:pl-[4.5rem] space-y-5">
                {c.paragraphs.map((p, i) =>
                  isPunchline(p) ? (
                    <p key={i} className="font-serif text-xl md:text-2xl italic font-medium text-emerald leading-snug">
                      {p}
                    </p>
                  ) : (
                    <p key={i} className="text-base md:text-lg leading-relaxed text-ink/75">
                      {p}
                    </p>
                  )
                )}
              </div>
              {STORY_PHOTOS[c.no] && (
                <Reveal className="mt-10 md:pl-[4.5rem]">
                  <figure className="relative max-w-2xl">
                    <div className="absolute -inset-2 border border-gold/30 translate-x-3 translate-y-3 pointer-events-none" />
                    <img
                      src={STORY_PHOTOS[c.no].src}
                      alt={STORY_PHOTOS[c.no].alt}
                      loading="lazy"
                      data-testid={`story-photo-${c.no}`}
                      className="relative w-full aspect-[16/10] object-cover bg-sand"
                    />
                    {STORY_PHOTOS[c.no].caption && (
                      <figcaption className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-sage">
                        {STORY_PHOTOS[c.no].caption}
                      </figcaption>
                    )}
                  </figure>
                </Reveal>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* Son */}
      <section className="relative overflow-hidden bg-emerald-deep text-ivory py-20 md:py-28">
        <div className="grain-overlay" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.4em] text-ivory/40">Son</p>
            <p className="mt-6 font-serif text-4xl sm:text-5xl font-semibold tracking-tight uppercase">
              Atılmaz <span className="italic text-gold">Saat</span>
            </p>
            <p className="mt-6 font-serif text-xl sm:text-2xl italic leading-snug text-ivory/80 max-w-2xl mx-auto">
              "2006'dan beri zamanı değil, yaşanmışlıkları da taşıyoruz."
            </p>
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={SITE.shopifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="story-shop-button"
                className="inline-flex items-center gap-3 rounded-full bg-ivory px-8 h-[52px] text-sm font-semibold text-emerald transition-colors duration-300 hover:bg-sand"
              >
                Koleksiyonu Keşfet
                <ArrowUpRight size={15} />
              </a>
              <button
                onClick={() => navigate("/")}
                data-testid="story-back-button"
                className="inline-flex items-center gap-3 rounded-full border border-ivory/25 px-8 h-[52px] text-sm font-medium text-ivory transition-colors duration-300 hover:border-ivory/60"
              >
                <ArrowLeft size={16} />
                Ana Sayfaya Dön
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
